import { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { motion, useInView, animate } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Hero } from '../components/home/Hero';
import { SuspenseFallback } from '../components/ui/SuspenseFallback';

// Lazy loading des composants lourds
const AllInOneSection = lazy(() => import('../components/home/AllInOneSection').then(module => ({
  default: module.AllInOneSection
})));

const RDCitiesMap = lazy(() => import('../components/home/RDCitiesMap').then(module => ({
  default: module.RDCitiesMap
})));

const MobileAppSection = lazy(() => import('../components/home/MobileAppSection').then(module => ({
  default: module.MobileAppSection
})));

const Testimonials = lazy(() => import('../components/home/Testimonials').then(module => ({
  default: module.Testimonials
})));

// ─── Sections du dot-nav ─────────────────────────────────────────────────────
const NAV_SECTIONS = [
  { id: 'section-hero',         label: 'Accueil' },
  { id: 'section-stats',        label: 'Chiffres clés' },
  { id: 'section-offer',        label: 'Notre offre' },
  { id: 'section-mobile',       label: 'Application mobile' },
  { id: 'section-map',          label: 'Coopératives en RDC' },
  { id: 'section-testimonials', label: 'Témoignages' },
  { id: 'section-cta',          label: 'Commencer' },
];

// ─── Barre de progression de scroll ──────────────────────────────────────────
function ScrollProgressBar() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[100] h-0.5 bg-transparent pointer-events-none">
      <div
        className="h-full bg-primary transition-[width] duration-100 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

// ─── Dot-navigation flottante ────────────────────────────────────────────────
function SectionDots() {
  const [active, setActive] = useState('section-hero');
  const [hovered, setHovered] = useState<string | null>(null);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(id); },
        { threshold: 0.35 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="fixed right-4 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col items-end gap-3">
      {NAV_SECTIONS.map(({ id, label }) => {
        const isActive = active === id;
        return (
          <div key={id} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => scrollTo(id)}>
            {/* Tooltip */}
            <span
              className={`text-[11px] font-medium text-gray-700 bg-white/90 backdrop-blur-sm border border-gray-200 px-2 py-0.5 rounded-full shadow-sm transition-all duration-200 whitespace-nowrap
                ${hovered === id ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none'}`}
            >
              {label}
            </span>
            {/* Dot */}
            <button
              onMouseEnter={() => setHovered(id)}
              onMouseLeave={() => setHovered(null)}
              aria-label={label}
              className={`rounded-full transition-all duration-300 ${
                isActive
                  ? 'w-3 h-3 bg-primary shadow-[0_0_0_3px_rgba(25,124,168,0.25)]'
                  : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── Wrapper d'animation d'entrée ────────────────────────────────────────────
function AnimatedSection({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <motion.div
      id={id}
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ─── Compteur animé ───────────────────────────────────────────────────────────
function AnimatedCounter({ value, suffix = '' }: { value: string; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const numericMatch = value.match(/\d+/);
  const numeric = numericMatch ? parseInt(numericMatch[0], 10) : null;
  const prefix = numericMatch ? value.slice(0, numericMatch.index) : '';
  const postfix = numericMatch ? value.slice((numericMatch.index ?? 0) + numericMatch[0].length) : value;

  useEffect(() => {
    if (!inView || numeric === null || !ref.current) return;
    const controls = animate(0, numeric, {
      duration: 1.4,
      ease: 'easeOut',
      onUpdate: (v) => {
        if (ref.current) ref.current.textContent = `${prefix}${Math.round(v)}${postfix}${suffix}`;
      },
    });
    return () => controls.stop();
  }, [inView, numeric, prefix, postfix, suffix]);

  return <span ref={ref}>{numeric === null ? value : `${prefix}0${postfix}${suffix}`}</span>;
}

// ─── Bande de crédibilité (chiffres clés) ────────────────────────────────────
const STAT_KEYS = [
  { value: '400+', labelKey: 'home.stats.supportedSMEs' },
  { value: '4',    labelKey: 'home.stats.activeCities' },
  { value: '100%', labelKey: 'home.stats.syscohadaCompliant' },
  { value: 'IA',   labelKey: 'home.stats.nativeCreditScoring' },
];

function StatsStrip() {
  const { t } = useTranslation();
  return (
    <div className="w-full bg-primary">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-white/20">
          {STAT_KEYS.map((s) => (
            <div key={s.labelKey} className="py-7 px-4 text-center">
              <p className="text-2xl sm:text-3xl font-extrabold text-white">
                <AnimatedCounter value={s.value} />
              </p>
              <p className="text-xs sm:text-sm text-blue-100 mt-1 font-medium">{t(s.labelKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── CTA final (clôture de page) ─────────────────────────────────────────────
function FinalCTA() {
  const { t } = useTranslation();
  return (
    <section className="w-full bg-gray-900 py-20 sm:py-24">
      <div className="mx-auto max-w-2xl px-4 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
          {t('home.cta.title')}
        </h2>
        <p className="text-gray-400 text-base leading-relaxed mb-8">
          {t('home.cta.subtitle')}
        </p>
        <Link
          to="/applications"
          className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-8 py-4 rounded-xl hover:bg-primary-hover transition-colors text-base shadow-lg"
        >
          {t('home.cta.button')}
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function Home() {
  return (
    <div className="bg-white">
      {/* Barre de progression globale */}
      <ScrollProgressBar />

      {/* Dot-navigation flottante (desktop) */}
      <SectionDots />

      {/* 1. Hook — accrocher */}
      <div id="section-hero">
        <Hero />
      </div>

      {/* 2. Crédibilité immédiate — chiffres clés */}
      <AnimatedSection id="section-stats">
        <StatsStrip />
      </AnimatedSection>

      {/* 3. Vue d'ensemble de l'offre */}
      <AnimatedSection id="section-offer">
        <Suspense fallback={<SuspenseFallback type="cards" />}>
          <AllInOneSection />
        </Suspense>
      </AnimatedSection>

      {/* 4. Le produit mobile */}
      <AnimatedSection id="section-mobile">
        <Suspense fallback={<SuspenseFallback type="list" />}>
          <MobileAppSection />
        </Suspense>
      </AnimatedSection>

      {/* 5. Positionnement + ancrage RDC (coopératives de détaillants) */}
      <AnimatedSection id="section-map">
        <Suspense fallback={<SuspenseFallback type="chart" />}>
          <RDCitiesMap />
        </Suspense>
      </AnimatedSection>

      {/* 7. Preuve sociale */}
      <AnimatedSection id="section-testimonials">
        <Suspense fallback={<SuspenseFallback type="cards" />}>
          <Testimonials />
        </Suspense>
      </AnimatedSection>

      {/* 8. Invitation finale à agir */}
      <AnimatedSection id="section-cta">
        <FinalCTA />
      </AnimatedSection>
    </div>
  );
}